import { useState, useMemo, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CityWithClusters, Cluster } from '@/types'

export interface CityClusterSelection {
    cityIds: number[]
    clusterIds: number[]
}

export interface MultiSelectCityFilterProps {
    label?: string
    selection: CityClusterSelection
    citiesWithClusters: CityWithClusters[]
    onChange: (selection: CityClusterSelection) => void
    loading?: boolean
}

export function MultiSelectCityFilter({ label, selection, citiesWithClusters, onChange, loading }: MultiSelectCityFilterProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [expandedCities, setExpandedCities] = useState<Set<number>>(new Set())
    const triggerRef = useRef<HTMLButtonElement>(null)
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })

    const allCitiesWithClusters = useMemo(() => {
        const cityMap = new Map<string, CityWithClusters>()
        for (const city of citiesWithClusters) {
            const normalizedName = city.name.trim().toUpperCase()
            const existing = cityMap.get(normalizedName)
            if (existing) {
                const existingClusterIds = new Set(existing.clusters.map(c => c.id))
                const newClusters = city.clusters.filter(c => !existingClusterIds.has(c.id))
                existing.clusters = [...existing.clusters, ...newClusters]
            } else {
                cityMap.set(normalizedName, { ...city, clusters: [...city.clusters] })
            }
        }
        return Array.from(cityMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    }, [citiesWithClusters])

    const toggleCityExpand = (cityId: number) => {
        const newExpanded = new Set(expandedCities)
        if (newExpanded.has(cityId)) newExpanded.delete(cityId)
        else newExpanded.add(cityId)
        setExpandedCities(newExpanded)
    }

    const handleCityToggle = (city: CityWithClusters) => {
        const isCitySelected = selection.cityIds.includes(city.id)
        if (isCitySelected) {
            onChange({
                cityIds: selection.cityIds.filter(id => id !== city.id),
                clusterIds: selection.clusterIds.filter(id => !city.clusters.some(c => c.id === id)),
            })
        } else {
            onChange({
                cityIds: [...selection.cityIds, city.id],
                clusterIds: selection.clusterIds.filter(id => !city.clusters.some(c => c.id === id)),
            })
        }
    }

    const handleClusterToggle = (cluster: Cluster, city: CityWithClusters) => {
        const isClusterSelected = selection.clusterIds.includes(cluster.id)
        const isCitySelected = selection.cityIds.includes(city.id)

        if (isCitySelected) {
            const otherClusters = city.clusters.filter(c => c.id !== cluster.id).map(c => c.id)
            onChange({
                cityIds: selection.cityIds.filter(id => id !== city.id),
                clusterIds: [...selection.clusterIds.filter(id => !city.clusters.some(c => c.id === id)), ...otherClusters],
            })
        } else if (isClusterSelected) {
            onChange({
                ...selection,
                clusterIds: selection.clusterIds.filter(id => id !== cluster.id),
            })
        } else {
            const newClusterIds = [...selection.clusterIds, cluster.id]
            const allClustersSelected = city.clusters.every(c => newClusterIds.includes(c.id))

            if (allClustersSelected) {
                onChange({
                    cityIds: [...selection.cityIds, city.id],
                    clusterIds: selection.clusterIds.filter(id => !city.clusters.some(c => c.id === id)),
                })
            } else {
                onChange({ ...selection, clusterIds: newClusterIds })
            }
        }
    }

    const handleClearAll = () => onChange({ cityIds: [], clusterIds: [] })
    const getSelectionCount = () => selection.cityIds.length + selection.clusterIds.length

    const getDisplayText = () => {
        const count = getSelectionCount()
        if (count === 0) return 'All Locations'
        if (count === 1) {
            if (selection.cityIds.length === 1) {
                const city = allCitiesWithClusters.find(c => c.id === selection.cityIds[0])
                return city?.name || 'All Locations'
            }
            for (const city of allCitiesWithClusters) {
                const cluster = city.clusters.find(c => c.id === selection.clusterIds[0])
                if (cluster) return cluster.name
            }
        }
        return `${count} Selected`
    }

    const isCityPartiallySelected = (city: CityWithClusters) => {
        if (selection.cityIds.includes(city.id)) return false
        return city.clusters.some(c => selection.clusterIds.includes(c.id))
    }

    useLayoutEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            setPos({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 280) })
        }
    }, [isOpen])

    return (
        <div className="relative w-full">
            {label && <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1 block">{label}</label>}
            <button
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "flex items-center justify-between gap-2 px-3 py-1.5 w-full rounded-lg border text-xs font-semibold transition-all duration-200 h-8",
                    "bg-white/50 dark:bg-white/10 backdrop-blur-sm hover:bg-white/70 dark:hover:bg-white/15",
                    isOpen ? "border-primary/50 shadow-sm ring-1 ring-primary/20" : "border-white/40 dark:border-white/20 hover:border-primary/30",
                    getSelectionCount() > 0 && "bg-primary/10 border-primary/30 text-primary"
                )}
            >
                <span className="truncate text-xs">{getDisplayText()}</span>
                {loading ? (
                    <Loader2 className="h-3 w-3 text-primary animate-spin flex-shrink-0" />
                ) : (
                    <ChevronDown className={cn("h-3 w-3 text-muted-foreground transition-transform duration-200 flex-shrink-0", isOpen && "rotate-180 text-primary")} />
                )}
            </button>
            {isOpen && createPortal(
                <>
                    <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
                    <div
                        className="fixed z-[9999] rounded-xl border border-border/50 bg-background/95 backdrop-blur-2xl shadow-xl animate-in fade-in slide-in-from-top-1 overflow-hidden"
                        style={{ top: pos.top, left: pos.left, width: pos.width }}
                    >
                        {getSelectionCount() > 0 && (
                            <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between bg-primary/5">
                                <span className="text-xs font-bold text-primary">
                                    {getSelectionCount()} Active Filters
                                </span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleClearAll()
                                    }}
                                    className="text-xs font-bold text-muted-foreground hover:text-destructive transition-colors"
                                >
                                    Clear all
                                </button>
                            </div>
                        )}

                        <div className="py-2 max-h-80 overflow-y-auto custom-scrollbar">
                            {allCitiesWithClusters.map((city) => {
                                const isCitySelected = selection.cityIds.includes(city.id)
                                const isPartial = isCityPartiallySelected(city)
                                const isExpanded = expandedCities.has(city.id)
                                const hasClusters = city.clusters.length > 0

                                return (
                                    <div key={city.id}>
                                        <div className={cn(
                                            "flex items-center gap-3 w-full px-4 py-3 hover:bg-accent/40 transition-colors cursor-pointer",
                                            (isCitySelected || isPartial) && "bg-primary/5"
                                        )} onClick={() => {
                                            if (hasClusters) toggleCityExpand(city.id)
                                            else handleCityToggle(city)
                                        }}>
                                            {hasClusters ? (
                                                <button onClick={(ev) => { ev.stopPropagation(); toggleCityExpand(city.id) }} className="p-1 hover:bg-background rounded-md shadow-sm border border-transparent hover:border-border/50 transition-all">
                                                    <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-300", isExpanded && "rotate-180 text-primary")} />
                                                </button>
                                            ) : (
                                                <div className="w-6" />
                                            )}

                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleCityToggle(city) }}
                                                className={cn(
                                                    "w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0",
                                                    isCitySelected ? "bg-primary border-primary shadow-sm shadow-primary/30" : isPartial ? "bg-primary/50 border-primary" : "border-muted-foreground/40 hover:border-primary/60 bg-background"
                                                )}
                                            >
                                                {(isCitySelected || isPartial) && <Check className="w-3.5 h-3.5 text-primary-foreground" strokeWidth={3} />}
                                            </button>

                                            <span className={cn("flex-1 text-sm font-semibold", (isCitySelected || isPartial) ? "text-primary" : "text-foreground")}>
                                                {city.name}
                                            </span>

                                            {hasClusters && (
                                                <span className="text-[10px] font-bold text-foreground bg-muted/60 dark:bg-white/10 px-2 py-0.5 rounded-full border border-border/30">
                                                    {city.clusters.length}
                                                </span>
                                            )}
                                        </div>

                                        {hasClusters && isExpanded && (
                                            <div className="ml-9 border-l-2 border-border/30 my-1 py-1">
                                                {city.clusters.map((cluster) => {
                                                    const isClusterSelected = isCitySelected || selection.clusterIds.includes(cluster.id)
                                                    return (
                                                        <button
                                                            key={cluster.id}
                                                            onClick={(e) => { e.stopPropagation(); handleClusterToggle(cluster, city) }}
                                                            className={cn(
                                                                "flex items-center gap-3 w-full pl-4 pr-4 py-2.5 text-left text-sm hover:bg-accent/40 transition-all",
                                                                isClusterSelected && "bg-primary/5"
                                                            )}
                                                        >
                                                            <div className={cn(
                                                                "w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0",
                                                                isClusterSelected ? "bg-primary border-primary shadow-sm" : "border-muted-foreground/40 bg-background"
                                                            )}>
                                                                {isClusterSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />}
                                                            </div>
                                                            <span className={cn("text-sm transition-colors", isClusterSelected ? "text-primary font-bold" : "text-muted-foreground font-medium")}>
                                                                {cluster.name}
                                                            </span>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    )
}
